import { useMemo } from 'react';
import { PageLoader } from '@/components/LoadingSpinner';
import { MultiRosterProvider } from '@/context/MultiRosterContext';
import { MyRosterContent } from '@/components/MyRosterContent';
import { useLeagueSettings } from '@/context/LeagueSettingsContext';
import { usePlayerData } from '@/hooks/usePlayerData';
import type { LeagueSettings as PlayerValueLeagueSettings } from '@/utils/playerEvaluation';
import type { LeagueSettings as TradeLeagueSettings } from '@/types/trade';

// Convert trade league settings to player value league settings
function convertTradeSettingsToPlayerValue(tradeSettings: TradeLeagueSettings): PlayerValueLeagueSettings {
  return {
    leagueSize: tradeSettings.teamCount,
    startingSlots: {
      QB: tradeSettings.roster.QB,
      RB: tradeSettings.roster.RB,
      WR: tradeSettings.roster.WR,
      TE: tradeSettings.roster.TE,
      K: tradeSettings.roster.K || 1,
      DEF: tradeSettings.roster.DEF || 1,
      FLEX: tradeSettings.roster.FLEX,
      SUPERFLEX: tradeSettings.roster.SUPERFLEX || 0
    }
  };
}

const MyRoster = () => {
  const { leagueData, hasImportedSettings } = useLeagueSettings();

  // Use league settings from context
  const activeLeagueSettings: PlayerValueLeagueSettings = useMemo(() => {
    return convertTradeSettingsToPlayerValue(leagueData.settings);
  }, [leagueData.settings]);

  // Use custom hook for player data management
  const { playersPool, isLoadingPlayers } = usePlayerData(
    activeLeagueSettings,
    hasImportedSettings,
    leagueData.settings
  );

  if (isLoadingPlayers) {
    return (
      <PageLoader
        pageName="My Roster"
        loadingText="Loading..."
      />
    );
  }

  return (
    <MultiRosterProvider playersPool={playersPool}>
      <MyRosterContent />
    </MultiRosterProvider>
  );
};

export default MyRoster;